import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, SpinnerIcon, PhotoIcon } from '../../Icons';
import { mapApiDataToProperty } from '../../../utils/propertyMappers';
import { PropertyImageSlider } from '../../public/shared/PropertyImageSlider';
import { DEFAULT_PROPERTY_IMAGE } from '../../../constants';

interface PropertyCardImageProps {
    initialImages: string[];
    propertyCode: string;
    source: string;
    isUpdated: boolean;
    codigoActual?: string;
    isSelected: boolean;
    isSelectionModeActive: boolean;
    onStatusDetected?: (status: string) => void;
}

export const PropertyCardImage: React.FC<PropertyCardImageProps> = ({
    initialImages, propertyCode, source, isUpdated, codigoActual, isSelected, isSelectionModeActive, onStatusDetected
}) => {
    const [images, setImages] = useState<string[]>(initialImages || []);
    const [loading, setLoading] = useState(false);
    const hasFetchedRef = React.useRef(false);

    const fetchFreshImages = useCallback(async () => {
        const searchCode = (isUpdated && codigoActual) ? codigoActual : propertyCode;
        if (!searchCode || hasFetchedRef.current) return;
        
        hasFetchedRef.current = true;
        setLoading(true);
        try {
            const res = await fetch(`https://api.ciencuadras.com/api/realestate?realEstateCode=${searchCode.trim()}&flag=0`);
            const json = await res.json();
            
            // Si la API no devuelve datos o indica 404
            if (!json.data || json.code === 404) {
                // Solo notificar 'Eliminado' si era explícitamente una actualización de código que ya no existe en ciencuadras
                if (onStatusDetected && isUpdated) {
                    onStatusDetected('Eliminado');
                }
                return;
            }

            if (json.data) {
                const mapped = mapApiDataToProperty(json.data);
                if (mapped.images && mapped.images.length > 0) {
                    setImages(mapped.images);
                }
                if (onStatusDetected && isUpdated) {
                    const apiStatus = String(json.data.estado || "").toLowerCase();
                    // Lógica extendida para detectar eliminación o inactividad desde la fuente original
                    if (json.data.activo === "0" || apiStatus.includes('eliminado')) {
                        onStatusDetected('Eliminado');
                    } else if (apiStatus.includes('vendido')) {
                        onStatusDetected('Vendido');
                    }
                }
            }
        } catch (e) { 
            console.error(e); 
        } finally { setLoading(false); }
    }, [propertyCode, codigoActual, isUpdated, onStatusDetected]);

    useEffect(() => {
        if (initialImages && initialImages.length > 0) {
            setImages(initialImages);
        }
        
        // Disparar búsqueda solo si:
        // 1. Es una actualización de código
        // 2. O no hay imágenes iniciales y tenemos un código
        if ((isUpdated && codigoActual) || ((!initialImages || initialImages.length === 0) && propertyCode)) {
            fetchFreshImages();
        }
    }, [initialImages, isUpdated, codigoActual, propertyCode, fetchFreshImages]);

    if (loading) return <div className="w-full h-full bg-gray-100 flex items-center justify-center"><SpinnerIcon className="w-8 h-8 text-sky-600 animate-spin"/></div>;
    
    // Fallback image if still empty
    const displayImages = images.length > 0 ? images : [DEFAULT_PROPERTY_IMAGE];

    return (
        <>
            <PropertyImageSlider 
                images={displayImages} 
                aspectRatio="h-full w-full" 
                showButtons={!isSelectionModeActive} 
                className="absolute inset-0"
            />
            {isSelectionModeActive && (
                <div className={`absolute inset-0 z-[20] flex items-center justify-center transition-all pointer-events-none ${isSelected ? 'bg-green-600/30' : 'bg-black/40'}`}>
                    <div className={`${isSelected ? 'bg-green-600' : 'bg-black/60'} px-4 py-2 rounded-xl border border-white/20 shadow-2xl`}>
                        <span className="text-white text-[10px] font-black uppercase">{isSelected ? 'Seleccionado' : 'Click para seleccionar'}</span>
                    </div>
                </div>
            )}
        </>
    );
};

